/**
 * ScheduleInsights — Advanced macro-schedule analytics
 * Tabs: Look-ahead · Earned Schedule · Float · Versions · What-If
 */
import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar as CalendarIcon, Clock, Target, GitBranch, History, TrendingUp, AlertTriangle, CheckCircle2, PlayCircle, Loader2, Trash2, RefreshCw, Sparkles, Edit3, Save, Undo2, FileSpreadsheet, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import api from '@/lib/api';
import LookAhead5DDrawer from '@/components/scheduling/LookAhead5DDrawer';

const surfacePanel = 'bg-white dark:bg-gradient-to-br dark:from-[#0c1222] dark:to-[#0a0f1e] border border-slate-200 dark:border-slate-700/50 rounded-xl';
const subPanel     = 'bg-slate-50 dark:bg-[#0a0f1e] border border-slate-200 dark:border-slate-700/40 rounded-lg';
const headTxt      = 'text-slate-800 dark:text-white';
const mutedTxt     = 'text-slate-500 dark:text-slate-400';
const bodyTxt      = 'text-slate-700 dark:text-slate-300';

// ═══════════════════════════════════════════════════
// AI INSIGHT PANEL — reusable across tabs
// ═══════════════════════════════════════════════════
function AIInsightPanel({ scheduleId, tab, data, disabled }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!scheduleId || !data || disabled) return;
    setLoading(true);
    try {
      const r = await api.post(`/scheduling/schedules/${scheduleId}/ai-insight`, { tab, data });
      setInsight(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI insight failed');
    } finally { setLoading(false); }
  };

  const riskColor = insight?.risk_level === 'HIGH' ? 'red' : insight?.risk_level === 'LOW' ? 'emerald' : 'amber';

  return (
    <div className={`${subPanel} p-3 mt-3`} data-testid={`ai-insight-${tab}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-xs font-semibold ${headTxt} flex items-center gap-1.5`}>
          <Sparkles className="w-3.5 h-3.5 text-fuchsia-500" /> AI Analysis
          {insight && <span className={`text-[10px] px-2 py-0.5 rounded-full bg-${riskColor}-100 dark:bg-${riskColor}-500/20 text-${riskColor}-700 dark:text-${riskColor}-300`}>{insight.risk_level}</span>}
        </div>
        <Button size="sm" onClick={run} disabled={loading || disabled}
                className="bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 text-white h-7 text-xs"
                data-testid={`ai-insight-run-${tab}`}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
          {insight ? 'Re-analyze' : 'Analyze with AI'}
        </Button>
      </div>
      {!insight && !loading && (
        <div className={`text-xs ${mutedTxt} italic`}>Click Analyze with AI for an executive-grade Cognara interpretation of this tabs data.</div>
      )}
      {insight && (
        <div className="space-y-2">
          <div className={`text-sm font-semibold ${headTxt}`}>{insight.headline}</div>
          <div>
            <div className={`text-[10px] uppercase tracking-wider ${mutedTxt} mb-1`}>Key Findings</div>
            <ul className={`text-xs ${bodyTxt} space-y-1 list-disc pl-4`}>
              {(insight.findings || []).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1`}>Recommendations</div>
            <ul className={`text-xs ${bodyTxt} space-y-1 list-disc pl-4`}>
              {(insight.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
          <div className={`text-[10px] ${mutedTxt}`}>Generated by Cognara at {insight.generated_at?.slice(11,16)} UTC</div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// LOOK-AHEAD

// ─────────────────────────────────────────────────────
// Resource breakdown sub-table shown when a Look-Ahead row expands.
// Full daily table per user preference (row-per-resource, column-per-day).
// ─────────────────────────────────────────────────────
function LookAheadResourceBreakdown({ resources }) {
  const rows = [
    ...(resources.manpower || []).map(r => ({ ...r, kind: 'LABOR' })),
    ...(resources.equipment || []).map(r => ({ ...r, kind: 'EQUIPMENT' })),
  ];
  if (!rows.length) return null;
  const dates = Array.from(new Set(rows.flatMap(r => (r.daily || []).map(d => d.date)))).sort();
  const dateIdx = Object.fromEntries(dates.map((d, i) => [d, i]));
  return (
    <div className="rounded border overflow-hidden" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}
         data-testid="lookahead-resource-breakdown">
      <div className="px-2 py-1 text-[10px] uppercase tracking-wider border-b flex items-center justify-between"
           style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
        <span>Manpower & Equipment · daily breakdown ({dates.length} day{dates.length !== 1 ? 's' : ''} in window)</span>
        <span>{rows.length} resource{rows.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="overflow-x-auto max-h-[280px]">
        <table className="text-[10px]" style={{ minWidth: '100%' }}>
          <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
            <tr>
              <th className="p-1 text-left font-medium sticky left-0 z-10" style={{ background: 'var(--card-bg-alt)', minWidth: 180, color: 'var(--text-muted)' }}>Resource</th>
              <th className="p-1 text-right font-medium" style={{ color: 'var(--text-muted)', minWidth: 60 }}>Total</th>
              {dates.map(d => (
                <th key={d} className="p-1 text-right font-mono font-medium whitespace-nowrap"
                    style={{ color: 'var(--text-muted)', minWidth: 44 }}>{d.slice(5)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const qtyByDate = Object.fromEntries((r.daily || []).map(d => [d.date, d.qty]));
              return (
                <tr key={`${r.kind}-${r.resourceId}`} className="border-t" style={{ borderColor: 'var(--border-color)' }}
                    data-testid={`lookahead-resource-${r.resourceId}`}>
                  <td className="p-1 sticky left-0 z-10 whitespace-nowrap" style={{ background: 'var(--card-bg)', color: 'var(--text-primary)' }}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${r.kind === 'LABOR' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    {r.name}
                  </td>
                  <td className="p-1 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{r.total.toFixed(1)}</td>
                  {dates.map(d => (
                    <td key={d} className="p-1 text-right font-mono" style={{ color: qtyByDate[d] ? 'var(--text-primary)' : 'var(--text-disabled)' }}>
                      {qtyByDate[d] ? qtyByDate[d].toFixed(1) : '·'}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// ═══════════════════════════════════════════════════
// LOOK-AHEAD REPORT
// ═══════════════════════════════════════════════════

// ═══════════════════════════════════════════════════
function LookAheadPanel({ scheduleId }) {
  const [windowWeeks, setWindowWeeks] = useState(12);
  const [data, setData] = useState(null);
  const [resources, setResources] = useState({});      // taskId → { manpower, equipment }
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('upcoming');
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [drilldownTaskId, setDrilldownTaskId] = useState(null);
  // Filters
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [hasLinks, setHasLinks] = useState('');       // '' | 'ANY' | 'DCI' | 'MCI' | 'QPT'
  const [discipline, setDiscipline] = useState('');    // csv
  // Tree mode
  const [treeMode, setTreeMode] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    try {
      const params = { window_weeks: windowWeeks, include_hierarchy: true };
      if (criticalOnly) params.critical_only = true;
      if (hasLinks) params.has_links = hasLinks;
      if (discipline) params.discipline = discipline;
      const [laRes, resRes] = await Promise.all([
        api.get(`/scheduling/schedules/${scheduleId}/lookahead`, { params }),
        api.get(`/scheduling/schedules/${scheduleId}/lookahead-resources`, { params: { window_weeks: Math.min(windowWeeks, 12) } }).catch(() => ({ data: { resourcesByTask: {} } })),
      ]);
      setData(laRes.data);
      setResources(resRes.data?.resourcesByTask || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Look-ahead failed');
    } finally { setLoading(false); }
  }, [scheduleId, windowWeeks, criticalOnly, hasLinks, discipline]);

  useEffect(() => { load(); }, [load]);

  // ── Build hierarchical rows (parents + leaves in wbs order) ──
  const items = data?.[view] || [];
  const treeRows = React.useMemo(() => {
    if (!treeMode) return items;
    const parents = (data?.hierarchy || []);
    const merged = [...parents, ...items];
    // Sort by wbs_code lexicographically after splitting into numbers
    const cmp = (a, b) => {
      const A = (a.wbs_code || '').split('.').map(x => parseInt(x, 10) || 0);
      const B = (b.wbs_code || '').split('.').map(x => parseInt(x, 10) || 0);
      for (let i = 0; i < Math.max(A.length, B.length); i++) {
        if ((A[i] || 0) !== (B[i] || 0)) return (A[i] || 0) - (B[i] || 0);
      }
      return 0;
    };
    return merged.sort(cmp);
  }, [treeMode, items, data]);

  const visibleRows = React.useMemo(() => {
    if (!treeMode) return items;
    // Hide any row whose ancestor is collapsed
    return treeRows.filter(r => {
      const parts = (r.wbs_code || '').split('.');
      for (let i = 1; i < parts.length; i++) {
        const anc = parts.slice(0, i).join('.');
        if (collapsed.has(anc)) return false;
      }
      return true;
    });
  }, [treeMode, treeRows, collapsed, items]);

  const toggleCollapse = (code) => setCollapsed(prev => {
    const nx = new Set(prev);
    if (nx.has(code)) nx.delete(code); else nx.add(code);
    return nx;
  });

  return (
    <div className={`${surfacePanel} p-4`} data-testid="lookahead-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <CalendarIcon className="w-4 h-4 text-sky-500" /> Look-Ahead Report
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Horizon: {data?.data_date?.slice(0,10)} → {data?.horizon?.slice(0,10)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className={`text-xs ${bodyTxt}`}>Window</Label>
          <Select value={String(windowWeeks)} onValueChange={(v) => setWindowWeeks(Number(v))}>
            <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1,2,3,4,6,8,12,16,24,52,104,260].map(n => <SelectItem key={n} value={String(n)}>{n === 260 ? '5 yrs' : n === 104 ? '2 yrs' : n === 52 ? '1 yr' : `${n} wk${n>1?'s':''}`}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="lookahead-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
        <button onClick={() => setCriticalOnly(v => !v)}
          className={`px-2 py-1 rounded-full border ${criticalOnly ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-500/20 dark:text-red-300' : 'text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600'}`}
          data-testid="lookahead-filter-critical">CP only</button>
        <button onClick={() => setHasLinks(hasLinks === 'ANY' ? '' : 'ANY')}
          className={`px-2 py-1 rounded-full border ${hasLinks === 'ANY' ? 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300 dark:bg-fuchsia-500/20 dark:text-fuchsia-300' : 'text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600'}`}
          data-testid="lookahead-filter-linked">Has links</button>
        {['DCI','MCI','QPT'].map(m => (
          <button key={m} onClick={() => setHasLinks(hasLinks === m ? '' : m)}
            className={`px-2 py-1 rounded-full border ${hasLinks === m ? 'bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-500/20 dark:text-sky-300' : 'text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600'}`}
            data-testid={`lookahead-filter-${m}`}>{m === 'DCI' ? 'Has Eng' : m === 'MCI' ? 'Has Proc' : 'Has F&C'}</button>
        ))}
        <input value={discipline} onChange={e => setDiscipline(e.target.value)}
          placeholder="Discipline codes (e.g. PIP,STR)"
          className="px-2 py-1 rounded border border-slate-300 dark:border-slate-600 bg-transparent text-xs w-52"
          data-testid="lookahead-filter-discipline" />
        <button onClick={() => setTreeMode(v => !v)}
          className={`ml-auto px-2 py-1 rounded-full border ${treeMode ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400 border-slate-300 dark:border-slate-600'}`}
          data-testid="lookahead-filter-tree">{treeMode ? 'Tree ▾' : 'Flat'}</button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {[
              { key: 'upcoming',    label: 'Upcoming',    color: 'sky',    n: data.counts.upcoming },
              { key: 'in_progress', label: 'In Progress', color: 'amber',  n: data.counts.in_progress },
              { key: 'finishing',   label: 'Finishing',   color: 'emerald',n: data.counts.finishing },
              { key: 'at_risk',     label: 'At Risk',     color: 'red',    n: data.counts.at_risk },
            ].map(c => (
              <button
                key={c.key}
                onClick={() => setView(c.key)}
                data-testid={`lookahead-tab-${c.key}`}
                className={`${subPanel} p-3 text-left transition ${view===c.key ? `ring-2 ring-${c.color}-500 border-${c.color}-400 dark:border-${c.color}-500/60` : 'hover:border-slate-300 dark:hover:border-slate-600'}`}
              >
                <div className={`text-[10px] uppercase tracking-wider text-${c.color}-600 dark:text-${c.color}-400`}>{c.label}</div>
                <div className={`text-2xl font-bold text-${c.color}-600 dark:text-${c.color}-300 mt-1`}>{c.n}</div>
              </button>
            ))}
          </div>

          <div className={`${subPanel} overflow-hidden`}>
            <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40 flex justify-between items-center`}>
              <span>{visibleRows.length} row{visibleRows.length !== 1 ? 's' : ''} — {view.replace('_',' ')}{treeMode ? ' · tree' : ''}</span>
            </div>
            <div className="max-h-[480px] overflow-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0">
                  <tr>
                    <th className={`p-2 text-center ${mutedTxt} w-6`}></th>
                    <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Task</th>
                    <th className={`p-2 text-center ${mutedTxt}`} title="Baseline">B Start</th>
                    <th className={`p-2 text-center ${mutedTxt}`} title="Baseline">B Finish</th>
                    <th className={`p-2 text-center ${mutedTxt}`} title="Forecast">F Start</th>
                    <th className={`p-2 text-center ${mutedTxt}`} title="Forecast">F Finish</th>
                    <th className={`p-2 text-center ${mutedTxt}`} title="Actual">A Start</th>
                    <th className={`p-2 text-center ${mutedTxt}`} title="Actual">A Finish</th>
                    <th className={`p-2 text-right ${mutedTxt}`}>% Done</th>
                    <th className={`p-2 text-center ${mutedTxt}`}>Links</th>
                    <th className={`p-2 text-center ${mutedTxt}`}>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr><td colSpan={12} className={`p-8 text-center ${mutedTxt}`}>Nothing in this bucket.</td></tr>
                  ) : visibleRows.map(t => {
                    const res = resources[t.task_id];
                    const hasResources = res && ((res.manpower?.length || 0) + (res.equipment?.length || 0)) > 0;
                    const isExpanded = expandedTaskId === t.task_id;
                    const depth = Math.max(0, (t.wbs_code || '').split('.').length - 1);
                    const isSummary = !!t.is_summary;
                    const isCollapsed = collapsed.has(t.wbs_code);
                    const lc = t.linked_counts || { eng: 0, proc: 0, fc: 0 };
                    const totalLinks = (lc.eng || 0) + (lc.proc || 0) + (lc.fc || 0);
                    return (
                    <Fragment key={t.task_id}>
                    <tr className={`border-t border-slate-200 dark:border-slate-700/30 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${isSummary ? 'bg-slate-50 dark:bg-slate-800/20 font-semibold' : 'cursor-pointer'}`}
                        onClick={() => !isSummary && hasResources && setExpandedTaskId(isExpanded ? null : t.task_id)}
                        data-testid={`lookahead-row-${t.task_id}`}>
                      <td className={`p-1.5 text-center ${mutedTxt}`}>
                        {isSummary
                          ? <button onClick={(e) => { e.stopPropagation(); toggleCollapse(t.wbs_code); }} data-testid={`lookahead-tree-toggle-${t.wbs_code}`}>
                              {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 inline"/> : <ChevronDown className="w-3.5 h-3.5 inline"/>}
                            </button>
                          : hasResources ? (isExpanded ? <ChevronDown className="w-3.5 h-3.5 inline"/> : <ChevronRight className="w-3.5 h-3.5 inline"/>) : ''}
                      </td>
                      <td className={`p-1.5 ${mutedTxt} font-mono`} style={{ paddingLeft: treeMode ? (8 + depth * 10) : undefined }}>{t.wbs_code}</td>
                      <td className={`p-1.5 ${headTxt}`}>{t.name}</td>
                      <td className={`p-1.5 text-center ${bodyTxt} whitespace-nowrap`}>{t.baseline_start?.slice(0,10) || '—'}</td>
                      <td className={`p-1.5 text-center ${bodyTxt} whitespace-nowrap`}>{t.baseline_finish?.slice(0,10) || '—'}</td>
                      <td className={`p-1.5 text-center ${bodyTxt} whitespace-nowrap`}>{t.forecast_start?.slice(0,10) || '—'}</td>
                      <td className={`p-1.5 text-center ${bodyTxt} whitespace-nowrap`}>{t.forecast_finish?.slice(0,10) || '—'}</td>
                      <td className={`p-1.5 text-center ${bodyTxt} whitespace-nowrap`}>{t.actual_start?.slice(0,10) || '—'}</td>
                      <td className={`p-1.5 text-center ${bodyTxt} whitespace-nowrap`}>{t.actual_finish?.slice(0,10) || '—'}</td>
                      <td className={`p-1.5 text-right ${bodyTxt}`}>{Math.round(t.percent_complete)}%</td>
                      <td className="p-1.5 text-center">
                        {totalLinks === 0 ? <span className={`text-[10px] ${mutedTxt}`}>—</span> : (
                          <span className="inline-flex gap-1" title={`Engineering: ${lc.eng} · Procurement: ${lc.proc} · F&C: ${lc.fc}`}>
                            <span className="text-[9px] px-1 py-0.5 rounded bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300 font-mono" data-testid={`lookahead-link-eng-${t.task_id}`}>E{lc.eng}</span>
                            <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 font-mono" data-testid={`lookahead-link-proc-${t.task_id}`}>P{lc.proc}</span>
                            <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-mono" data-testid={`lookahead-link-fc-${t.task_id}`}>C{lc.fc}</span>
                          </span>
                        )}
                      </td>
                      <td className="p-1.5 text-center">
                        {t.is_critical && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 mr-1">CP</span>}
                        {!isSummary && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDrilldownTaskId(t.task_id); }}
                            className="text-[9.5px] px-2 py-0.5 rounded-full bg-fuchsia-100 dark:bg-fuchsia-500/20 text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-200 dark:hover:bg-fuchsia-500/30 inline-flex items-center gap-0.5"
                            data-testid={`lookahead-5d-btn-${t.task_id}`}
                            title="Open 5D drilldown"
                          >
                            <Layers className="w-2.5 h-2.5" /> 5D
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && hasResources && (
                      <tr className="border-t border-slate-200 dark:border-slate-700/30">
                        <td colSpan={12} className="p-3 bg-slate-50 dark:bg-slate-800/20">
                          <LookAheadResourceBreakdown resources={res} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
          <AIInsightPanel scheduleId={scheduleId} tab="lookahead" data={data} />
        </>
      )}
      {drilldownTaskId && (
        <LookAhead5DDrawer
          scheduleId={scheduleId}
          taskId={drilldownTaskId}
          windowWeeks={windowWeeks}
          onClose={() => setDrilldownTaskId(null)}
        />
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════
// ═══════════════════════════════════════════════════
// EARNED SCHEDULE
// ═══════════════════════════════════════════════════
function EarnedSchedulePanel({ scheduleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    try {
      const res = await api.get(`/scheduling/schedules/${scheduleId}/earned-schedule`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Earned-schedule failed');
    } finally { setLoading(false); }
  }, [scheduleId]);

  useEffect(() => { load(); }, [load]);

  const statusColor = (s) => s === 'AHEAD' ? 'emerald' : s === 'BEHIND' ? 'red' : 'amber';

  return (
    <div className={`${surfacePanel} p-4`} data-testid="earned-schedule-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <Target className="w-4 h-4 text-fuchsia-500" /> Earned Schedule Analysis
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Walt Lipke method — time-based EVM (SPI(t), SV(t), forecast completion)</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="es-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {data && !data.error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Data Date</div>
              <div className={`text-lg font-bold ${headTxt} mt-1`}>{data.data_date?.slice(0,10)}</div>
            </div>
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Earned Schedule</div>
              <div className={`text-lg font-bold text-fuchsia-600 dark:text-fuchsia-300 mt-1`}>{data.earned_schedule?.slice(0,10)}</div>
            </div>
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>SPI(t)</div>
              <div className={`text-lg font-bold ${data.spi_t >= 1 ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'} mt-1`} data-testid="es-spi-t">{data.spi_t?.toFixed(2)}</div>
            </div>
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>SV(t) days</div>
              <div className={`text-lg font-bold ${data.sv_t_days >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'} mt-1`}>{data.sv_t_days > 0 ? '+' : ''}{data.sv_t_days?.toFixed(1)}</div>
            </div>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 mb-3`}>
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Planned Finish</div>
              <div className={`text-base font-semibold ${headTxt} mt-1`}>{data.project_finish?.slice(0,10)}</div>
            </div>
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Forecast Finish</div>
              <div className={`text-base font-semibold text-amber-600 dark:text-amber-300 mt-1`} data-testid="es-forecast">{data.forecast_finish?.slice(0,10)}</div>
              <div className={`text-[10px] ${mutedTxt} mt-1`}>({data.forecast_slip_days > 0 ? '+' : ''}{data.forecast_slip_days}d vs plan)</div>
            </div>
            <div className={`${subPanel} p-3`}>
              <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Status</div>
              <div className={`text-base font-semibold text-${statusColor(data.status)}-600 dark:text-${statusColor(data.status)}-300 mt-1`}>{data.status}</div>
            </div>
          </div>

          <div className={`${subPanel} p-3 text-sm ${bodyTxt}`}>{data.narrative}</div>
          <AIInsightPanel scheduleId={scheduleId} tab="earned" data={data} />
        </>
      )}
      {data?.error && <div className={`text-sm ${mutedTxt} p-4`}>{data.error}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// FLOAT ANALYSIS
// ═══════════════════════════════════════════════════
function FloatPanel({ scheduleId }) {
  const [threshold, setThreshold] = useState(5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    try {
      const res = await api.get(`/scheduling/schedules/${scheduleId}/float-analysis`, { params: { near_critical_threshold_days: threshold } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Float analysis failed');
    } finally { setLoading(false); }
  }, [scheduleId, threshold]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className={`${surfacePanel} p-4`} data-testid="float-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <TrendingUp className="w-4 h-4 text-red-500" /> Float & Near-Critical Analysis
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Total-float distribution + near-critical path identification</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className={`text-xs ${bodyTxt}`}>Near-critical ≤</Label>
          <Input type="number" min={0} max={60} step={0.5} value={threshold} onChange={(e) => setThreshold(parseFloat(e.target.value) || 5)} className="w-20 h-8" />
          <span className={`text-xs ${mutedTxt}`}>days</span>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="float-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className={`${subPanel} p-3 border-l-4 border-l-red-500`}>
              <div className={`text-[10px] uppercase tracking-wider text-red-600 dark:text-red-400`}>Critical (float ≤ 0)</div>
              <div className={`text-2xl font-bold text-red-600 dark:text-red-300 mt-1`} data-testid="float-critical">{data.totals.critical}</div>
            </div>
            <div className={`${subPanel} p-3 border-l-4 border-l-amber-500`}>
              <div className={`text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400`}>Near-Critical</div>
              <div className={`text-2xl font-bold text-amber-600 dark:text-amber-300 mt-1`} data-testid="float-near-critical">{data.totals.near_critical}</div>
            </div>
            <div className={`${subPanel} p-3 border-l-4 border-l-sky-500`}>
              <div className={`text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400`}>Moderate (≤ 5d)</div>
              <div className={`text-2xl font-bold text-sky-600 dark:text-sky-300 mt-1`}>{data.totals.moderate}</div>
            </div>
            <div className={`${subPanel} p-3 border-l-4 border-l-emerald-500`}>
              <div className={`text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400`}>High Float</div>
              <div className={`text-2xl font-bold text-emerald-600 dark:text-emerald-300 mt-1`}>{data.totals.high_float}</div>
            </div>
          </div>

          <div className={`${subPanel} p-3 mb-3 text-sm ${bodyTxt}`}>{data.narrative}</div>

          {(data.near_critical || []).length > 0 && (
            <div className={`${subPanel} overflow-hidden`}>
              <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>
                Top {Math.min(data.near_critical.length, 100)} Near-Critical Tasks (ordered by lowest float)
              </div>
              <div className="max-h-[380px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0">
                    <tr>
                      <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                      <th className={`p-2 text-left ${mutedTxt}`}>Task</th>
                      <th className={`p-2 text-left ${mutedTxt}`}>Start</th>
                      <th className={`p-2 text-left ${mutedTxt}`}>Finish</th>
                      <th className={`p-2 text-right ${mutedTxt}`}>Float (d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.near_critical.slice(0, 100).map(t => (
                      <tr key={t.task_id} className="border-t border-slate-200 dark:border-slate-700/30">
                        <td className={`p-2 ${mutedTxt} font-mono`}>{t.wbs_code}</td>
                        <td className={`p-2 ${headTxt}`}>{t.name}</td>
                        <td className={`p-2 ${bodyTxt}`}>{t.scheduled_start?.slice(0,10)}</td>
                        <td className={`p-2 ${bodyTxt}`}>{t.scheduled_finish?.slice(0,10)}</td>
                        <td className={`p-2 text-right text-amber-600 dark:text-amber-300 font-semibold`}>{t.float_days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <AIInsightPanel scheduleId={scheduleId} tab="float" data={data} />
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════
// VERSIONS + AUDIT TRAIL
// ═══════════════════════════════════════════════════
function VersionsPanel({ scheduleId, onRefresh }) {
  const [versions, setVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newNote, setNewNote] = useState('');
  const [diff, setDiff] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setLoading(true);
    try {
      const [v, a] = await Promise.all([
        api.get(`/scheduling/schedules/${scheduleId}/versions`),
        api.get(`/scheduling/schedules/${scheduleId}/audit-log`, { params: { limit: 100 } }),
      ]);
      setVersions(v.data);
      setAudit(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load versions');
    } finally { setLoading(false); }
  }, [scheduleId]);

  useEffect(() => { load(); }, [load]);

  const createVersion = async () => {
    try {
      await api.post(`/scheduling/schedules/${scheduleId}/versions`, { label: newLabel, note: newNote });
      toast.success('Version created');
      setShowNew(false); setNewLabel(''); setNewNote('');
      load();
    } catch (e) { toast.error('Failed to create version'); }
  };

  const showDiff = async (vid) => {
    try {
      const r = await api.get(`/scheduling/schedules/${scheduleId}/versions/${vid}/diff`);
      setDiff(r.data);
      setDiffOpen(true);
    } catch (e) { toast.error('Failed to compute diff'); }
  };

  const restore = async (vid, label) => {
    if (!window.confirm(`Restore schedule to version "${label}"? Current state will be auto-backed up first.`)) return;
    try {
      const r = await api.post(`/scheduling/schedules/${scheduleId}/versions/${vid}/restore`);
      toast.success(`Restored — ${r.data.tasks} tasks re-installed`);
      onRefresh && onRefresh();
      load();
    } catch (e) { toast.error('Restore failed'); }
  };

  return (
    <div className={`${surfacePanel} p-4`} data-testid="versions-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <History className="w-4 h-4 text-indigo-500" /> Versions & Audit Trail
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Snapshot the schedule, compare, restore</p>
        </div>
        <Button size="sm" className="bg-indigo-500 hover:bg-indigo-600 text-white h-8" onClick={() => setShowNew(true)} data-testid="new-version-btn">
          <History className="w-3.5 h-3.5 mr-1" /> Snapshot Now
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Versions list */}
        <div className={`${subPanel}`}>
          <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>Versions ({versions.length})</div>
          <div className="max-h-[420px] overflow-auto">
            {versions.length === 0 ? (
              <div className={`p-6 text-center text-xs ${mutedTxt}`}>No versions yet.</div>
            ) : versions.map(v => (
              <div key={v.id} className="p-3 border-b border-slate-200 dark:border-slate-700/30 hover:bg-slate-100 dark:hover:bg-slate-800/40">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${headTxt} truncate`}>{v.label}</div>
                    <div className={`text-[11px] ${mutedTxt}`}>{v.created_at?.slice(0,16).replace('T',' ')} · {v.created_by} · {v.task_count} tasks</div>
                    {v.note && <div className={`text-[11px] ${bodyTxt} mt-0.5 italic`}>{v.note}</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => showDiff(v.id)} className="h-7 px-2 text-xs" data-testid={`version-diff-${v.id}`}>Diff</Button>
                    <Button size="sm" variant="ghost" onClick={() => restore(v.id, v.label)} className="h-7 px-2 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`version-restore-${v.id}`}>Restore</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Audit trail */}
        <div className={`${subPanel}`}>
          <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>Audit Trail ({audit.length})</div>
          <div className="max-h-[420px] overflow-auto">
            {audit.length === 0 ? (
              <div className={`p-6 text-center text-xs ${mutedTxt}`}>No events yet.</div>
            ) : audit.map(a => (
              <div key={a.id} className="p-2.5 border-b border-slate-200 dark:border-slate-700/30 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300`}>{a.action}</span>
                  <span className={mutedTxt}>{a.at?.slice(0,16).replace('T',' ')}</span>
                </div>
                <div className={`mt-0.5 ${bodyTxt}`}>{a.label || a.task_id || a.scenario_id || '—'} · {a.user}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* New version */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-lg">
          <DialogHeader><DialogTitle className={headTxt}>Snapshot Schedule</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className={`text-xs ${bodyTxt}`}>Label</Label><Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Baseline post-MoM 05-Feb" /></div>
            <div><Label className={`text-xs ${bodyTxt}`}>Note (optional)</Label><Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="What changed?" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button className="bg-indigo-500 hover:bg-indigo-600 text-white" onClick={createVersion} data-testid="snapshot-save-btn">Create Snapshot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diff dialog */}
      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-4xl">
          <DialogHeader><DialogTitle className={headTxt}>Diff — {diff?.label}</DialogTitle></DialogHeader>
          {diff && (
            <div className="max-h-[60vh] overflow-auto space-y-3">
              <div className={`grid grid-cols-3 gap-2 text-xs`}>
                <div className={`${subPanel} p-3 text-center`}><div className={mutedTxt}>Added</div><div className={`text-xl font-bold text-emerald-600 dark:text-emerald-300`}>{diff.totals.added}</div></div>
                <div className={`${subPanel} p-3 text-center`}><div className={mutedTxt}>Changed</div><div className={`text-xl font-bold text-amber-600 dark:text-amber-300`}>{diff.totals.changed}</div></div>
                <div className={`${subPanel} p-3 text-center`}><div className={mutedTxt}>Removed</div><div className={`text-xl font-bold text-red-600 dark:text-red-300`}>{diff.totals.removed}</div></div>
              </div>
              {diff.changed?.length > 0 && (
                <div className={`${subPanel} overflow-hidden`}>
                  <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>Changed tasks</div>
                  <div className="max-h-[300px] overflow-auto">
                    {diff.changed.slice(0, 80).map(c => (
                      <div key={c.id} className="p-2 border-b border-slate-200 dark:border-slate-700/30 text-xs">
                        <div className={`font-semibold ${headTxt}`}>{c.wbs_code} · {c.name}</div>
                        {Object.entries(c.deltas).map(([f, v]) => (
                          <div key={f} className={`${bodyTxt} mt-0.5`}>
                            <span className={mutedTxt}>{f}:</span> <span className="text-red-500 dark:text-red-400 line-through">{String(v.old ?? '—').slice(0,30)}</span> → <span className="text-emerald-600 dark:text-emerald-400">{String(v.new ?? '—').slice(0,30)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AIInsightPanel scheduleId={scheduleId} tab="versions" data={{ versions, audit }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════
// WHAT-IF SCENARIOS
// ═══════════════════════════════════════════════════
function WhatIfPanel({ scheduleId, onRefresh }) {
  const [scenarios, setScenarios] = useState([]);
  const [compare, setCompare] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newNote, setNewNote] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorScenario, setEditorScenario] = useState(null);
  const [editorTasks, setEditorTasks] = useState([]);
  const [editorFilter, setEditorFilter] = useState('');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState({});

  const load = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const res = await api.get(`/scheduling/schedules/${scheduleId}/scenarios`);
      setScenarios(res.data);
    } catch (e) { /* silent */ }
  }, [scheduleId]);

  useEffect(() => { load(); }, [load]);

  const createScenario = async () => {
    try {
      const r = await api.post(`/scheduling/schedules/${scheduleId}/scenarios`, { label: newLabel, note: newNote });
      toast.success(`Scenario cloned with ${r.data.task_count} tasks`);
      setShowNew(false); setNewLabel(''); setNewNote('');
      load();
    } catch (e) { toast.error('Clone failed'); }
  };

  const openCompare = async (sid) => {
    try {
      const r = await api.get(`/scheduling/scenarios/${sid}/compare`);
      setCompare(r.data);
      setCompareOpen(true);
    } catch (e) { toast.error('Compare failed'); }
  };

  const promote = async (sid, label) => {
    if (!window.confirm(`Promote "${label}" to become the live schedule? Current will be auto-backed up.`)) return;
    try {
      await api.post(`/scheduling/scenarios/${sid}/promote`);
      toast.success('Scenario promoted to live');
      onRefresh && onRefresh();
      load();
    } catch (e) { toast.error('Promote failed'); }
  };

  const del = async (sid) => {
    if (!window.confirm('Delete this scenario?')) return;
    try {
      await api.delete(`/scheduling/scenarios/${sid}`);
      toast.success('Scenario deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const openEditor = async (scenario) => {
    setEditorScenario(scenario);
    setEditorOpen(true);
    setEditorLoading(true);
    try {
      const r = await api.get(`/scheduling/scenarios/${scenario.id}/tasks`);
      setEditorTasks(r.data);
    } catch (e) {
      toast.error('Failed to load scenario tasks');
    } finally { setEditorLoading(false); }
  };

  const patchScenarioTask = async (taskId, patch) => {
    if (!editorScenario) return;
    setEditorSaving(prev => ({ ...prev, [taskId]: true }));
    try {
      const r = await api.patch(`/scheduling/scenarios/${editorScenario.id}/tasks/${taskId}`, patch);
      setEditorTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...r.data } : t));
      toast.success('Task updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setEditorSaving(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const shiftByDays = async (taskId, days) => {
    const t = editorTasks.find(x => x.id === taskId);
    if (!t) return;
    const shift = (iso) => {
      if (!iso) return iso;
      const d = new Date(iso);
      d.setDate(d.getDate() + days);
      return d.toISOString();
    };
    await patchScenarioTask(taskId, {
      scheduled_start: shift(t.scheduled_start),
      scheduled_finish: shift(t.scheduled_finish),
    });
  };

  const filteredEditorTasks = editorFilter
    ? editorTasks.filter(t => (t.name || '').toLowerCase().includes(editorFilter.toLowerCase())
                         || (t.wbs_code || '').toLowerCase().includes(editorFilter.toLowerCase()))
    : editorTasks;

  const exportCompareToExcel = () => {
    if (!compare) return;
    try {
      const wb = XLSX.utils.book_new();
      const summaryRows = [
        ['Metric', 'Baseline', 'Scenario', 'Delta'],
        ['Start',       compare.baseline.start?.slice(0,10)  || '',  compare.scenario.start?.slice(0,10)  || '', ''],
        ['Finish',      compare.baseline.finish?.slice(0,10) || '',  compare.scenario.finish?.slice(0,10) || '', (compare.impact.finish_delta_days || 0) + 'd'],
        ['Duration',    compare.baseline.duration_days + 'd',        compare.scenario.duration_days + 'd',       (compare.impact.duration_delta_days || 0) + 'd'],
        ['Budget',      compare.baseline.budget,                     compare.scenario.budget,                    compare.impact.budget_delta || 0],
        ['Critical Tasks', compare.baseline.critical_count,          compare.scenario.critical_count,            compare.impact.critical_delta || 0],
        ['Task Count',  compare.baseline.task_count,                 compare.scenario.task_count,                (compare.scenario.task_count - compare.baseline.task_count)],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
      ws1['!cols'] = [{ wch: 24 }, { wch: 22 }, { wch: 22 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Impact Summary');

      const rows = [['WBS', 'Task', 'Field', 'Baseline', 'Scenario']];
      (compare.task_deltas || []).forEach(td => {
        const fields = Object.keys(td.deltas || {});
        if (fields.length === 0) return;
        fields.forEach((f, i) => {
          rows.push([
            i === 0 ? (td.wbs_code || '') : '',
            i === 0 ? (td.name || '') : '',
            f,
            String(td.deltas[f].base ?? ''),
            String(td.deltas[f].scen ?? ''),
          ]);
        });
      });
      const ws2 = XLSX.utils.aoa_to_sheet(rows);
      ws2['!cols'] = [{ wch: 12 }, { wch: 42 }, { wch: 18 }, { wch: 24 }, { wch: 24 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Task Deltas');

      const safeLabel = (compare.label || 'scenario').replace(/[^a-z0-9-_ ]/gi, '').slice(0, 40);
      const fname = `Scenario_Compare_${safeLabel}_${new Date().toISOString().slice(0,10)}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success('Excel file downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div className={`${surfacePanel} p-4`} data-testid="whatif-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt} flex items-center gap-2`}>
            <GitBranch className="w-4 h-4 text-purple-500" /> What-If Scenarios
          </h3>
          <p className={`text-xs ${mutedTxt}`}>Clone the schedule · test changes · compare outcomes · promote when ready</p>
        </div>
        <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white h-8" onClick={() => setShowNew(true)} data-testid="new-scenario-btn">
          <GitBranch className="w-3.5 h-3.5 mr-1" /> New Scenario
        </Button>
      </div>

      <div className={`${subPanel} overflow-hidden`}>
        <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>Active Scenarios ({scenarios.length})</div>
        {scenarios.length === 0 ? (
          <div className={`p-8 text-center text-xs ${mutedTxt}`}>No scenarios. Clone the live schedule to test changes without affecting production.</div>
        ) : scenarios.map(s => (
          <div key={s.id} className="p-3 border-b border-slate-200 dark:border-slate-700/30 flex items-center justify-between">
            <div>
              <div className={`text-sm font-semibold ${headTxt}`}>{s.label}</div>
              <div className={`text-[11px] ${mutedTxt}`}>Cloned {s.created_at?.slice(0,10)} · {s.task_count} tasks · Status: <span className={s.status==='PROMOTED' ? 'text-emerald-600 dark:text-emerald-400' : 'text-purple-600 dark:text-purple-400'}>{s.status}</span></div>
              {s.note && <div className={`text-[11px] ${bodyTxt} italic mt-0.5`}>{s.note}</div>}
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={() => openEditor(s)} className="h-7 text-xs" data-testid={`scenario-edit-${s.id}`}>
                <Edit3 className="w-3 h-3 mr-1" /> Edit Tasks
              </Button>
              <Button size="sm" variant="outline" onClick={() => openCompare(s.id)} className="h-7 text-xs" data-testid={`scenario-compare-${s.id}`}>Compare</Button>
              {s.status !== 'PROMOTED' && (
                <Button size="sm" onClick={() => promote(s.id, s.label)} className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`scenario-promote-${s.id}`}>
                  <PlayCircle className="w-3 h-3 mr-1" /> Promote
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => del(s.id)} className="h-7 text-xs text-red-500" data-testid={`scenario-delete-${s.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* New scenario */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-lg">
          <DialogHeader><DialogTitle className={headTxt}>Clone as New Scenario</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className={`text-xs ${bodyTxt}`}>Label</Label><Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. WhatIf: FTP 2-week delay" /></div>
            <div><Label className={`text-xs ${bodyTxt}`}>Note</Label><Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="What are you testing?" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button className="bg-purple-500 hover:bg-purple-600 text-white" onClick={createScenario} data-testid="clone-scenario-btn">Clone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-4xl">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className={headTxt}>Compare — {compare?.label}</DialogTitle>
              {compare && (
                <Button size="sm" variant="outline" onClick={exportCompareToExcel} className="h-7 text-xs" data-testid="compare-export-btn">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1 text-emerald-600 dark:text-emerald-400" /> Export to Excel
                </Button>
              )}
            </div>
          </DialogHeader>
          {compare && (
            <div className="max-h-[60vh] overflow-auto space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className={`${subPanel} p-3`}>
                  <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Baseline</div>
                  <div className={`text-sm ${headTxt}`}>Start: {compare.baseline.start?.slice(0,10)}</div>
                  <div className={`text-sm ${headTxt}`}>Finish: {compare.baseline.finish?.slice(0,10)}</div>
                  <div className={`text-sm ${bodyTxt}`}>Duration: {compare.baseline.duration_days}d</div>
                  <div className={`text-sm ${bodyTxt}`}>Budget: ${compare.baseline.budget.toLocaleString()}</div>
                  <div className={`text-sm ${bodyTxt}`}>Critical: {compare.baseline.critical_count}</div>
                </div>
                <div className={`${subPanel} p-3 border-l-4 border-l-purple-500`}>
                  <div className={`text-[10px] uppercase tracking-wider text-purple-600 dark:text-purple-400`}>Scenario</div>
                  <div className={`text-sm ${headTxt}`}>Start: {compare.scenario.start?.slice(0,10)}</div>
                  <div className={`text-sm ${headTxt}`}>Finish: {compare.scenario.finish?.slice(0,10)}</div>
                  <div className={`text-sm ${bodyTxt}`}>Duration: {compare.scenario.duration_days}d</div>
                  <div className={`text-sm ${bodyTxt}`}>Budget: ${compare.scenario.budget.toLocaleString()}</div>
                  <div className={`text-sm ${bodyTxt}`}>Critical: {compare.scenario.critical_count}</div>
                </div>
              </div>
              <div className={`${subPanel} p-3`}>
                <div className={`text-[10px] uppercase tracking-wider ${mutedTxt} mb-2`}>Impact</div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div><span className={mutedTxt}>Finish shift:</span> <span className={compare.impact.finish_delta_days > 0 ? 'text-red-500' : 'text-emerald-500'}>{compare.impact.finish_delta_days > 0 ? '+' : ''}{compare.impact.finish_delta_days}d</span></div>
                  <div><span className={mutedTxt}>Duration Δ:</span> <span className={bodyTxt}>{compare.impact.duration_delta_days}d</span></div>
                  <div><span className={mutedTxt}>Budget Δ:</span> <span className={bodyTxt}>${compare.impact.budget_delta.toLocaleString()}</span></div>
                  <div><span className={mutedTxt}>Critical Δ:</span> <span className={bodyTxt}>{compare.impact.critical_delta}</span></div>
                </div>
              </div>
              {compare.task_deltas?.length > 0 && (
                <div className={`${subPanel} overflow-hidden`}>
                  <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>{compare.task_changes_count} tasks changed</div>
                  <div className="max-h-[300px] overflow-auto">
                    {compare.task_deltas.slice(0, 80).map(td => (
                      <div key={td.task_id} className="p-2 border-b border-slate-200 dark:border-slate-700/30 text-xs">
                        <div className={`font-semibold ${headTxt}`}>{td.wbs_code} · {td.name}</div>
                        {Object.entries(td.deltas).map(([f, v]) => (
                          <div key={f} className={`${bodyTxt} mt-0.5`}>
                            <span className={mutedTxt}>{f}:</span> <span className="text-red-500 line-through">{String(v.base ?? '—').slice(0,30)}</span> → <span className="text-emerald-600">{String(v.scen ?? '—').slice(0,30)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scenario Editor — inline task grid */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-6xl">
          <DialogHeader>
            <DialogTitle className={headTxt}>
              <Edit3 className="w-4 h-4 inline-block mr-1.5 -mt-0.5 text-purple-500" />
              Scenario Editor · {editorScenario?.label}
            </DialogTitle>
            <p className={`text-[11px] ${mutedTxt}`}>Edit any leaf tasks dates, duration, %, priority, or activity type. Changes stay isolated to this scenario until you Promote it.</p>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            <Input value={editorFilter} onChange={(e) => setEditorFilter(e.target.value)} placeholder="Filter by WBS or name…" className="h-8 text-xs" data-testid="editor-filter" />
            <span className={`text-[11px] ${mutedTxt}`}>{filteredEditorTasks.length} of {editorTasks.length} tasks</span>
          </div>

          {editorLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-auto border border-slate-200 dark:border-slate-700 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0 z-10">
                  <tr>
                    <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Task</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Type</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Start</th>
                    <th className={`p-2 text-left ${mutedTxt}`}>Finish</th>
                    <th className={`p-2 text-right ${mutedTxt}`}>Dur (d)</th>
                    <th className={`p-2 text-right ${mutedTxt}`}>%</th>
                    <th className={`p-2 text-center ${mutedTxt}`}>Priority</th>
                    <th className={`p-2 text-center ${mutedTxt}`}>Quick Shift</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEditorTasks.slice(0, 500).map(t => (
                    <tr key={t.id} className={`border-t border-slate-200 dark:border-slate-700/30 ${t.is_critical ? 'bg-red-50 dark:bg-red-500/10' : t.is_near_critical ? 'bg-amber-50 dark:bg-amber-500/10' : ''}`}>
                      <td className={`p-1.5 ${mutedTxt} font-mono text-[10px]`}>{t.wbs_code}</td>
                      <td className={`p-1.5 ${headTxt} max-w-[220px] truncate`} title={t.name}>{t.name}</td>
                      <td className="p-1.5">
                        <Select value={t.activity_type || 'TASK'} onValueChange={(v) => patchScenarioTask(t.id, { activity_type: v })}>
                          <SelectTrigger className="h-6 text-[10px] px-1 w-24 border-slate-300 dark:border-slate-600"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['TASK','LOE','HAMMOCK','MILESTONE'].map(v => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1.5">
                        <Input type="date" value={(t.scheduled_start || '').slice(0,10)}
                               onChange={(e) => patchScenarioTask(t.id, { scheduled_start: e.target.value + 'T00:00:00Z' })}
                               className="h-6 text-[10px] px-1 w-28" data-testid={`editor-start-${t.id}`} />
                      </td>
                      <td className="p-1.5">
                        <Input type="date" value={(t.scheduled_finish || '').slice(0,10)}
                               onChange={(e) => patchScenarioTask(t.id, { scheduled_finish: e.target.value + 'T00:00:00Z' })}
                               className="h-6 text-[10px] px-1 w-28" data-testid={`editor-finish-${t.id}`} />
                      </td>
                      <td className="p-1.5 text-right">
                        <Input type="number" value={t.duration_unit === 'days' ? (t.duration || 0) : Math.round((t.duration || 0) / 8)}
                               onChange={(e) => patchScenarioTask(t.id, { duration: parseFloat(e.target.value) || 0 })}
                               className="h-6 text-[10px] px-1 w-14 text-right" />
                      </td>
                      <td className="p-1.5 text-right">
                        <Input type="number" min={0} max={100} value={Math.round(t.percent_complete || 0)}
                               onChange={(e) => patchScenarioTask(t.id, { percent_complete: parseFloat(e.target.value) || 0 })}
                               className="h-6 text-[10px] px-1 w-14 text-right" />
                      </td>
                      <td className="p-1.5 text-center">
                        <Select value={t.priority || 'medium'} onValueChange={(v) => patchScenarioTask(t.id, { priority: v })}>
                          <SelectTrigger className="h-6 text-[10px] px-1 w-20 border-slate-300 dark:border-slate-600"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['critical','high','medium','low'].map(v => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => shiftByDays(t.id, -7)} disabled={editorSaving[t.id]}>-7d</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => shiftByDays(t.id, -1)} disabled={editorSaving[t.id]}>-1d</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-amber-600 dark:text-amber-400" onClick={() => shiftByDays(t.id, 1)} disabled={editorSaving[t.id]}>+1d</Button>
                          <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-amber-600 dark:text-amber-400" onClick={() => shiftByDays(t.id, 7)} disabled={editorSaving[t.id]}>+7d</Button>
                          {editorSaving[t.id] && <Loader2 className="w-3 h-3 animate-spin text-purple-500" />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEditorTasks.length > 500 && <div className={`p-3 text-center text-[11px] ${mutedTxt}`}>Showing first 500 tasks — narrow the filter to see more.</div>}
            </div>
          )}

          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <div className={`text-[11px] ${mutedTxt}`}>Changes save on-the-fly. Click Compare back on the list to review impact.</div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setEditorOpen(false)}>Close</Button>
                <Button className="bg-purple-500 hover:bg-purple-600 text-white" onClick={() => { setEditorOpen(false); openCompare(editorScenario.id); }} data-testid="editor-compare-btn">
                  <PlayCircle className="w-3.5 h-3.5 mr-1" /> Compare Impact
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AIInsightPanel scheduleId={scheduleId} tab="whatif" data={{ scenarios, compare }} disabled={scenarios.length === 0} />
    </div>
  );
}
// ═══════════════════════════════════════════════════
// TOP-LEVEL WRAPPER
// ═══════════════════════════════════════════════════
export default function ScheduleInsights({ scheduleId, onRefresh }) {
  const [tab, setTab] = useState('lookahead');
  const [rollupOpen, setRollupOpen] = useState(false);
  const [rollupPreview, setRollupPreview] = useState(null);
  const [rollupResult, setRollupResult] = useState(null);
  const [rollupBusy, setRollupBusy] = useState(false);

  const previewRollup = async () => {
    setRollupBusy(true); setRollupResult(null);
    try {
      const r = await api.get(`/scheduling/schedules/${scheduleId}/rollup/preview`);
      setRollupPreview(r.data);
      setRollupOpen(true);
    } catch (e) { toast.error('Preview failed'); }
    finally { setRollupBusy(false); }
  };

  const applyRollup = async () => {
    setRollupBusy(true);
    try {
      const r = await api.post(`/scheduling/schedules/${scheduleId}/rollup`);
      setRollupResult(r.data);
      toast.success(`Rollup complete: ${r.data.summaries_rolled} summary tasks · ${r.data.hammocks_recomputed} hammocks`);
      onRefresh && onRefresh();
    } catch (e) { toast.error('Rollup failed'); }
    finally { setRollupBusy(false); }
  };

  const undoRollup = async () => {
    if (!rollupResult?.undo_version_id) return;
    if (!window.confirm(`Undo the rollup by restoring the auto-backup "${rollupResult.undo_version_label}"?`)) return;
    setRollupBusy(true);
    try {
      const r = await api.post(`/scheduling/schedules/${scheduleId}/versions/${rollupResult.undo_version_id}/restore`);
      toast.success(`Rollup undone — ${r.data.tasks} tasks restored`);
      setRollupOpen(false);
      setRollupResult(null);
      setRollupPreview(null);
      onRefresh && onRefresh();
    } catch (e) { toast.error('Undo failed'); }
    finally { setRollupBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${headTxt}`}>Schedule Insights</h3>
          <p className={`text-xs ${mutedTxt}`}>Look-ahead · Earned Schedule · Float · Versions · What-If</p>
        </div>
        <Button size="sm" variant="outline" onClick={previewRollup} disabled={rollupBusy} data-testid="rollup-btn">
          {rollupBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Roll-up Summaries & Hammocks
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 dark:bg-[#0c1222] border border-slate-200 dark:border-slate-700/50 p-0.5">
          <TabsTrigger value="lookahead" className="text-xs px-3 py-1.5" data-testid="tab-lookahead"><CalendarIcon className="w-3.5 h-3.5 mr-1.5" /> Look-Ahead</TabsTrigger>
          <TabsTrigger value="earned" className="text-xs px-3 py-1.5" data-testid="tab-earned-schedule"><Target className="w-3.5 h-3.5 mr-1.5" /> Earned Schedule</TabsTrigger>
          <TabsTrigger value="float" className="text-xs px-3 py-1.5" data-testid="tab-float"><TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Float</TabsTrigger>
          <TabsTrigger value="versions" className="text-xs px-3 py-1.5" data-testid="tab-versions"><History className="w-3.5 h-3.5 mr-1.5" /> Versions</TabsTrigger>
          <TabsTrigger value="whatif" className="text-xs px-3 py-1.5" data-testid="tab-whatif"><GitBranch className="w-3.5 h-3.5 mr-1.5" /> What-If</TabsTrigger>
        </TabsList>

        <TabsContent value="lookahead" className="mt-3 data-[state=inactive]:hidden" forceMount><LookAheadPanel scheduleId={scheduleId} /></TabsContent>
        <TabsContent value="earned" className="mt-3 data-[state=inactive]:hidden" forceMount><EarnedSchedulePanel scheduleId={scheduleId} /></TabsContent>
        <TabsContent value="float" className="mt-3 data-[state=inactive]:hidden" forceMount><FloatPanel scheduleId={scheduleId} /></TabsContent>
        <TabsContent value="versions" className="mt-3 data-[state=inactive]:hidden" forceMount><VersionsPanel scheduleId={scheduleId} onRefresh={onRefresh} /></TabsContent>
        <TabsContent value="whatif" className="mt-3 data-[state=inactive]:hidden" forceMount><WhatIfPanel scheduleId={scheduleId} onRefresh={onRefresh} /></TabsContent>
      </Tabs>

      {/* Rollup Detailed Dialog */}
      <Dialog open={rollupOpen} onOpenChange={setRollupOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-4xl">
          <DialogHeader>
            <DialogTitle className={headTxt}>
              <RefreshCw className="w-4 h-4 inline-block mr-1.5 -mt-0.5 text-sky-500" />
              Roll-up Summaries & Hammocks
            </DialogTitle>
            <p className={`text-[11px] ${mutedTxt}`}>Preview all summary/hammock recalculations before applying them.</p>
          </DialogHeader>

          {rollupPreview && !rollupResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className={`${subPanel} p-3 text-center`}>
                  <div className={`text-[10px] uppercase tracking-wider ${mutedTxt}`}>Summary Tasks</div>
                  <div className={`text-2xl font-bold ${headTxt}`}>{rollupPreview.summary_tasks_total}</div>
                </div>
                <div className={`${subPanel} p-3 text-center`}>
                  <div className={`text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400`}>Changes Needed</div>
                  <div className={`text-2xl font-bold text-amber-600 dark:text-amber-300`}>{rollupPreview.changes_needed}</div>
                </div>
                <div className={`${subPanel} p-3 text-center`}>
                  <div className={`text-[10px] uppercase tracking-wider text-fuchsia-600 dark:text-fuchsia-400`}>Hammocks to Recompute</div>
                  <div className={`text-2xl font-bold text-fuchsia-600 dark:text-fuchsia-300`}>{rollupPreview.hammocks_to_recompute}</div>
                </div>
              </div>

              {rollupPreview.proposed_changes?.length > 0 && (
                <div className={`${subPanel} overflow-hidden`}>
                  <div className={`px-3 py-2 text-[10px] uppercase tracking-wider ${mutedTxt} border-b border-slate-200 dark:border-slate-700/40`}>
                    Proposed Changes (showing up to 100 tasks)
                  </div>
                  <div className="max-h-[45vh] overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-800/40 sticky top-0">
                        <tr>
                          <th className={`p-2 text-left ${mutedTxt}`}>WBS</th>
                          <th className={`p-2 text-left ${mutedTxt}`}>Summary Task</th>
                          <th className={`p-2 text-left ${mutedTxt}`}>Field</th>
                          <th className={`p-2 text-left ${mutedTxt}`}>Old</th>
                          <th className={`p-2 text-left ${mutedTxt}`}>New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rollupPreview.proposed_changes.slice(0, 100).flatMap(c =>
                          Object.entries(c.deltas).map(([f, v], i) => (
                            <tr key={`${c.task_id}-${f}`} className="border-t border-slate-200 dark:border-slate-700/30">
                              {i === 0 ? (
                                <>
                                  <td className={`p-2 ${mutedTxt} font-mono text-[10px]`} rowSpan={Object.keys(c.deltas).length}>{c.wbs_code}</td>
                                  <td className={`p-2 ${headTxt}`} rowSpan={Object.keys(c.deltas).length}>{c.name}</td>
                                </>
                              ) : null}
                              <td className={`p-2 ${mutedTxt}`}>{f}</td>
                              <td className={`p-2 text-red-500 dark:text-red-400`}>{String(v.old ?? '—').slice(0,30)}</td>
                              <td className={`p-2 text-emerald-600 dark:text-emerald-400 font-semibold`}>{String(v.new ?? '—').slice(0,30)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {rollupResult && (
            <div className={`${subPanel} p-4 space-y-2`}>
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
                <div className={`text-lg font-semibold`}>Rollup Applied Successfully</div>
              </div>
              <div className={`grid grid-cols-3 gap-3 text-sm ${bodyTxt}`}>
                <div>Summaries flagged: <span className={`font-bold ${headTxt}`}>{rollupResult.summaries_flagged}</span></div>
                <div>Summaries rolled: <span className={`font-bold ${headTxt}`}>{rollupResult.summaries_rolled}</span></div>
                <div>Hammocks recomputed: <span className={`font-bold ${headTxt}`}>{rollupResult.hammocks_recomputed}</span></div>
              </div>
              {rollupResult.undo_version_id && (
                <div className={`mt-2 p-2 rounded border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 flex items-center justify-between`}>
                  <div className={`text-xs ${bodyTxt}`}>
                    <Undo2 className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-amber-600 dark:text-amber-400" />
                    Auto-backup saved as <span className={`font-semibold ${headTxt}`}>{rollupResult.undo_version_label}</span> — restore any time from the Versions tab.
                  </div>
                  <Button size="sm" onClick={undoRollup} disabled={rollupBusy} className="bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs" data-testid="rollup-undo-btn">
                    <Undo2 className="w-3 h-3 mr-1" /> Undo Now
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRollupOpen(false)}>Close</Button>
            {!rollupResult && (
              <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={applyRollup} disabled={rollupBusy} data-testid="rollup-apply-btn">
                {rollupBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Apply Rollup
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
